import React, { useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import "./event-signup.css";

// Initialize Stripe with your publishable key
// IMPORTANT: Add this to your .env.local file: REACT_APP_STRIPE_PUBLISHABLE_KEY=pk_test_xxx
const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;

// Payment Form Component (uses Stripe hooks)
function PaymentForm({ formData, role }) {
  const stripe = useStripe();
  const elements = useElements();
  const functions = getFunctions();
  
  const [localFormData, setLocalFormData] = useState(formData);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [step, setStep] = useState(1); // 1 = info, 2 = payment

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLocalFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNextStep = (e) => {
    e.preventDefault();
    
    // Validation
    if (!localFormData.name || !localFormData.email || !localFormData.phone || !localFormData.mdSmd) {
      setErrorMessage("Please fill in all fields");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(localFormData.email)) {
      setErrorMessage("Please enter a valid email address");
      return;
    }

    setErrorMessage("");
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      setErrorMessage("Payment system not loaded. Please refresh the page.");
      return;
    }

    setSubmitting(true);
    setErrorMessage("");

    try {
      // Step 1: Create SetupIntent on backend
      const createSetupIntent = httpsCallable(functions, 'createSetupIntent');
      const setupResult = await createSetupIntent({
        email: localFormData.email,
        name: localFormData.name,
      });

      const { clientSecret, customerId, setupIntentId } = setupResult.data;

      // Step 2: Confirm card setup with Stripe
      const cardElement = elements.getElement(CardElement);
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: localFormData.name,
            email: localFormData.email,
            phone: localFormData.phone,
          },
        },
      });

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      // Step 3: Save payment method to Firestore
      const savePaymentMethod = httpsCallable(functions, 'savePaymentMethod');
      await savePaymentMethod({
        setupIntentId: setupIntent.id,
        customerId: customerId,
        userInfo: localFormData,
      });

      // Success!
      setShowSuccessModal(true);
      setLocalFormData({
        name: "",
        email: "",
        phone: "",
        mdSmd: ""
      });
      setStep(1);
      
    } catch (error) {
      console.error("Error submitting signup:", error);
      setErrorMessage(error.message || "Failed to submit request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const isAdmin = role === "Admin";
  const copyPublicLink = () => {
    const publicUrl = `${window.location.origin}/event-signup`;
    navigator.clipboard.writeText(publicUrl).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 3000);
    }).catch(err => {
      console.error("Failed to copy:", err);
      alert(`Copy this link: ${publicUrl}`);
    });
  };

  return (
    <div className="event-signup-page">
      <div className="event-signup-container">
        {/* Admin Link Generator */}
        {isAdmin && (
          <div className="admin-link-generator">
            <div className="link-gen-content">
              <div className="link-gen-info">
                <h3 className="link-gen-title">🔗 Public Registration Link</h3>
                <p className="link-gen-subtitle">Share this link for QR code or direct access</p>
                <code className="public-link-display">{window.location.origin}/event-signup</code>
              </div>
              <button
                type="button"
                onClick={copyPublicLink}
                className={`copy-link-btn ${linkCopied ? 'copied' : ''}`}
              >
                {linkCopied ? '✓ Copied!' : '📋 Copy Link'}
              </button>
            </div>
          </div>
        )}

        <div className="event-signup-header">
          <h1 className="event-signup-title">Upgrade Your Account</h1>
          <p className="event-signup-subtitle">Join the future of Wealth Builders</p>
          {step === 2 && (
            <p className="step-indicator">Step 2 of 2: Payment Information</p>
          )}
        </div>

        {step === 1 ? (
          <form onSubmit={handleNextStep} className="event-signup-form">
            {/* Name Field */}
            <div className="form-group">
              <label htmlFor="name" className="form-label">
                Full Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={localFormData.name}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter your full name"
                required
              />
            </div>

            {/* Email Field */}
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email <span className="required">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={localFormData.email}
                onChange={handleInputChange}
                className="form-input"
                placeholder="your.email@example.com"
                required
              />
            </div>

            {/* Phone Field */}
            <div className="form-group">
              <label htmlFor="phone" className="form-label">
                Phone Number <span className="required">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={localFormData.phone}
                onChange={handleInputChange}
                className="form-input"
                placeholder="(555) 555-5555"
                required
              />
            </div>

            {/* SMD Field */}
            <div className="form-group">
              <label htmlFor="mdSmd" className="form-label">
                Choose Your SMD <span className="required">*</span>
              </label>
              <input
                type="text"
                id="mdSmd"
                name="mdSmd"
                value={localFormData.mdSmd}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter your SMD's name"
                required
              />
            </div>

            {errorMessage && (
              <div className="message message-error">
                ✗ {errorMessage}
              </div>
            )}

            <button type="submit" className="submit-btn">
              Continue to Payment →
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="event-signup-form">
            <div className="payment-info-box">
              <h3>Your Information</h3>
              <p><strong>Name:</strong> {localFormData.name}</p>
              <p><strong>Email:</strong> {localFormData.email}</p>
              <button 
                type="button" 
                className="edit-info-btn"
                onClick={() => setStep(1)}
              >
                ← Edit Information
              </button>
            </div>

            {/* Payment Method Field */}
            <div className="form-group">
              <label className="form-label">
                Credit Card Information <span className="required">*</span>
              </label>
              <div className="signup-notice">
                <div className="notice-icon">ℹ️</div>
                <div className="notice-text">
                  <strong>This is only a signup form for the website.</strong> You will NOT be charged now. 
                  We're securely saving your payment information for when your account is ready to be upgraded.
                </div>
              </div>
              <div className="card-element-wrapper">
                <CardElement
                  options={{
                    style: {
                      base: {
                        fontSize: '16px',
                        color: '#fff',
                        '::placeholder': {
                          color: 'rgba(255, 255, 255, 0.4)',
                        },
                        backgroundColor: 'transparent',
                      },
                      invalid: {
                        color: '#ef5350',
                      },
                    },
                  }}
                />
              </div>
              <p className="payment-note">
                🔒 Your card details are encrypted and stored securely by Stripe. We never see your full card number.
              </p>
            </div>

            {errorMessage && (
              <div className="message message-error">
                ✗ {errorMessage}
              </div>
            )}

            <button
              type="submit"
              className="submit-btn"
              disabled={submitting || !stripe}
            >
              {submitting ? "Processing..." : "Complete Registration"}
            </button>
          </form>
        )}
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="success-modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="success-glow"></div>
            <h2 className="success-modal-title">Welcome to the Future of Wealth Builders</h2>
            <button 
              className="success-modal-close"
              onClick={() => setShowSuccessModal(false)}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Main Component
export default function EventSignup() {
  const { role } = useAuth();
  const [formData] = useState({
    name: "",
    email: "",
    phone: "",
    mdSmd: ""
  });

  return (
    <Elements stripe={stripePromise}>
      <PaymentForm
        formData={formData}
        role={role}
      />
    </Elements>
  );
}

